import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "./ThemeProvider";

const NAV_LINKS = [
  { id: "services", label: "Services" },
  { id: "transformation", label: "Transformation" },
  { id: "chatbot", label: "AI Chat Board" },
  { id: "enquiry", label: "Enquiry" },
];

export const Navigation = () => {
  const { theme, toggle } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      // After route change, smooth scroll
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goHome = () => {
    if (location.pathname !== "/") navigate("/");
    else window.scrollTo({ top: 0, behavior: "smooth" });
    setOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div
          className={`flex items-center justify-between rounded-2xl px-4 md:px-6 py-3 transition-all duration-500 ${
            scrolled
              ? "dark:glass-dark glass-light shadow-lg"
              : "bg-transparent"
          }`}
        >
          {/* Logo */}
          <button
            onClick={goHome}
            className="flex items-center gap-2.5 group"
            data-testid="nav-logo"
          >
            <div className="relative h-11 w-11 rounded-xl bg-white dark:bg-white/95 grid place-items-center shadow-[0_8px_24px_rgba(255,122,0,0.25)] group-hover:scale-105 transition-transform p-1">
              <img
                src="/digindia-logo.png"
                alt="DIGINDIA PAYTECH"
                className="h-full w-full object-contain"
              />
              <span className="absolute -bottom-1 -right-1 h-3 w-3 rounded-full bg-[#00C853] border-2 border-background" />
            </div>
            <div className="leading-none text-left">
              <div className="font-heading font-extrabold tracking-tight text-base md:text-lg">
                DIGINDIA
              </div>
              <div className="text-[10px] md:text-[11px] tracking-[0.2em] text-muted-foreground font-medium">
                PAYTECH
              </div>
            </div>
          </button>

          {/* Desktop Links */}
          <nav className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="px-4 py-2 text-sm font-medium rounded-full hover:bg-foreground/5 transition-colors"
                data-testid={`nav-link-${link.id}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <button
              onClick={toggle}
              className="h-10 w-10 grid place-items-center rounded-full border border-border hover:bg-foreground/5 transition-all hover:scale-105"
              aria-label="Toggle theme"
              data-testid="theme-toggle"
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
                  animate={{ rotate: 0, opacity: 1, scale: 1 }}
                  exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
                  transition={{ duration: 0.25 }}
                  className="grid place-items-center"
                >
                  {theme === "dark" ? (
                    <Sun className="h-4 w-4 text-[#FF9933]" />
                  ) : (
                    <Moon className="h-4 w-4" />
                  )}
                </motion.span>
              </AnimatePresence>
            </button>

            <button
              onClick={() => scrollTo("enquiry")}
              className="hidden md:inline-flex items-center gap-2 h-10 px-5 rounded-full gradient-saffron text-white font-semibold text-sm shadow-[0_8px_24px_rgba(255,122,0,0.3)] hover:shadow-[0_12px_32px_rgba(255,122,0,0.5)] hover:scale-[1.03] transition-all"
              data-testid="nav-cta-button"
            >
              Get Started
              <span className="h-1.5 w-1.5 rounded-full bg-[#00C853]" />
            </button>

            <button
              className="md:hidden h-10 w-10 grid place-items-center rounded-full border border-border"
              onClick={() => setOpen((o) => !o)}
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="md:hidden mt-2 dark:glass-dark glass-light rounded-2xl p-3 flex flex-col"
              data-testid="mobile-menu"
            >
              {NAV_LINKS.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="text-left px-4 py-3 rounded-xl hover:bg-foreground/5 font-medium"
                  data-testid={`mobile-nav-link-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("enquiry")}
                className="mt-2 h-11 rounded-xl gradient-saffron text-white font-semibold"
                data-testid="mobile-nav-cta"
              >
                Get Started
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navigation;
